import React, { ReactNode } from 'react';
import { formatBytes } from '../../utilities/format';
import type { StorageFile } from './types';

interface Props {
  /** Text shown in the panel heading (the current path). */
  pathLabel: string;
  files: StorageFile[];
  loading: boolean;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  onNavigate: (path: string) => void;
  /** Omit to hide the "up one level" button. */
  onLevelUp?: () => void;
  /** Show the "No files found!" alert when the list is empty. */
  showEmptyAlert?: boolean;
  emptyMessage?: ReactNode;
  maxHeight?: number;
}

// The Angular file-browser panel: a heading showing the current path with an optional
// level-up button, a list-group of entries (single-click selects, double-click opens a
// directory), and an optional "No files found!" alert.
export function FileBrowser({
  pathLabel,
  files,
  loading,
  selectedPath,
  onSelect,
  onNavigate,
  onLevelUp,
  showEmptyAlert = false,
  emptyMessage,
  maxHeight = 200,
}: Props) {
  return (
    <div>
      <div className="panel panel-default">
        <div className="panel-heading">
          <i className="fas fa-folder-open" aria-hidden="true" /> {pathLabel}
          {onLevelUp && (
            <button
              type="button"
              className="btn btn-default btn-xs pull-right"
              onClick={onLevelUp}
              disabled={loading}
            >
              <i className="fas fa-level-up-alt" aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="list-group" style={{ overflowY: 'auto', maxHeight }}>
          {loading && (
            <div className="list-group-item text-muted">
              <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading…
            </div>
          )}
          {!loading && files.map(f => (
            <a
              key={f.path}
              className={`list-group-item${selectedPath === f.path ? ' active' : ''}${f.isDirectory ? ' directory' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => onSelect(f.path)}
              onDoubleClick={() => { if (f.isDirectory) onNavigate(f.path); }}
            >
              <i className={`fas ${f.isDirectory ? 'fa-folder' : 'fa-file'}`} aria-hidden="true" /> {f.name}
              {!f.isDirectory && f.size !== undefined && (
                <span className="label label-default pull-right">{formatBytes(f.size)}</span>
              )}
            </a>
          ))}
        </div>
      </div>

      {showEmptyAlert && !loading && files.length === 0 && (
        <div className="alert alert-block alert-info">
          <h4>No files found!</h4>
          {emptyMessage}
        </div>
      )}
    </div>
  );
}
